import { CommonModule } from '@angular/common';
import { Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService, CanPipe } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import { isEqual, isNil } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  DisableControlDirective,
  InputLabelComponent,
  LoaderComponent,
  MbsPopupType,
  MbsSize,
  ModalService,
  ModalSettings,
  OverlayDirective,
  SidepanelBase,
  SidepanelComponent,
  SwitcherComponent,
  SwitcherDescription,
  TabsetContentDirective,
  TabsetContentOutlet,
  TabsetDirective,
  TabsetItemDirective,
  TabsetLinkDirective,
  ToastService,
  TypedTemplateDirective
} from 'mbs-ui-kit';
import { DownloadBuildModule } from 'mbs-ui-shared@components/download-build/download-build.module';
import { DownloadConnectModule } from 'mbs-ui-shared@components/download-connect/download-connect.module';
import { QuickRestoreSchemaModalComponent } from 'mbs-ui-shared@components/quick-restore-schema-modal/quick-restore-schema-modal.component';
import { GuidesTabComponent } from 'mbs-ui-shared@components/sidepanel-downloads/tabs/guides/guides-tab.component';
import { CopyingStatus, getTabsConfig } from 'mbs-ui-shared@constants';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BrandsFacade } from 'mbs-ui-shared@facades/brands.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { Administrator, BuildType } from 'mbs-ui-shared@models';
import Brand from 'mbs-ui-shared@models/Brand';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { Build, BuildDetails } from 'mbs-ui-shared@models/build';
import { ComputersStatisticModel } from 'mbs-ui-shared@models/computersStatistic';
import { UtilityModalSelectEntityType } from 'mbs-ui-shared@modules/utility-selection-modal/const';
import { UtilitySelectionModalComponent } from 'mbs-ui-shared@modules/utility-selection-modal/utility-selection-modal.component';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { ComputersStatisticService } from 'mbs-ui-shared@services/computersStatistic.service';
import { BehaviorSubject, Observable, from, map, mergeMap, noop, of } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { AddComputerComponent } from '../add-computer/add-computer.component';
import { AddComputerModule } from '../add-computer/add-computer.module';

export const DPSpecificMessageKey = 'dp_specific_build';

@UntilDestroy()
@Component({
  selector: 'app-sidepanel-global-downloads',
  templateUrl: './sidepanel-downloads.component.html',
  styleUrls: ['./sidepanel-downloads.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    AddComputerModule,
    CanPipe,
    ReactiveFormsModule,
    DownloadBuildModule,
    DownloadConnectModule,
    GuidesTabComponent,
    LoaderComponent,
    AlertComponent,
    I18NextPipe,
    InputLabelComponent,
    SidepanelComponent,
    SwitcherComponent,
    TabsetContentDirective,
    TabsetContentOutlet,
    TabsetDirective,
    TabsetItemDirective,
    TabsetLinkDirective,
    DisableControlDirective,
    OverlayDirective,
    TypedTemplateDirective,
    SwitcherDescription
  ],
  standalone: true
})
export class SidepanelDownloadsComponent extends SidepanelBase implements OnInit {
  private buildService = inject(BuildService);
  private auth = inject(AuthFacade);
  private brandsFacade = inject(BrandsFacade);
  private toastService = inject(ToastService);
  modalService = inject(ModalService);
  private ability = inject(AbilityService);
  i18nextService = inject(I18NextService);
  i18nPipe = inject(I18NextPipe);
  buildsFacade = inject(BuildsFacade);
  computersFacade = inject(ComputersFacade);
  private computersStatisticService = inject(ComputersStatisticService);

  public readonly agentType = AgentType;
  public readonly keys = Object.keys;
  public readonly alertSize = MbsSize;
  public readonly alertType = MbsPopupType;
  public readonly copyingStatus = CopyingStatus;
  public readonly elementSelectors = {
    name: {
      [AgentType.Backup]: {
        tabBlock: 'downloads-panel--backup-tab',
        tabBlockBtn: 'downloads-panel--backup-tab-btn',
        tabIcon: 'downloads-panel--backup-tab-icon',
        tabImage: 'downloads-panel--backup-tab-image',
        tabTitle: 'downloads-panel--backup-tab-title',
        downloadLink: 'downloads-panel--backup-tab-download-link',
        specificCloseBtn: 'downloads-panel--backup-tab-specific-close-btn',
        helpLink: 'downloads-panel--backup-tab-help-link'
      },
      [AgentType.RMM]: {
        tabBlock: 'downloads-panel--rmm-tab',
        tabBlockBtn: 'downloads-panel--rmm-tab-btn',
        tabIcon: 'downloads-panel--rmm-tab-icon',
        tabImage: 'downloads-panel--rmm-tab-image',
        tabTitle: 'downloads-panel--rmm-tab-title',
        downloadLink: 'downloads-panel--rmm-tab-download-link',
        specificCloseBtn: 'downloads-panel--rmm-tab-specific-close-btn',
        helpLink: 'downloads-panel--rmm-tab-help-link'
      },
      [AgentType.RA]: {
        tabBlock: 'downloads-panel--ra-tab',
        tabBlockBtn: 'downloads-panel--ra-tab-btn',
        tabIcon: 'downloads-panel--ra-tab-icon',
        tabImage: 'downloads-panel--ra-tab-image',
        tabTitle: 'downloads-panel--ra-tab-title',
        downloadLink: 'downloads-panel--ra-tab-download-link',
        specificCloseBtn: 'downloads-panel--ra-tab-specific-close-btn',
        helpLink: 'downloads-panel--ra-tab-help-link'
      },
      [AgentType.NetworkDiscovery]: {
        tabBlock: 'downloads-panel--nd-tab',
        tabBlockBtn: 'downloads-panel--nd-tab-btn',
        tabIcon: 'downloads-panel--nd-tab-icon',
        tabImage: 'downloads-panel--nd-tab-image',
        tabTitle: 'downloads-panel--nd-tab-title',
        downloadLink: 'downloads-panel--nd-tab-download-link',
        specificCloseBtn: 'downloads-panel--nd-tab-specific-close-btn',
        helpLink: 'downloads-panel--nd-tab-help-link'
      },
      bootableUSB: {
        tabBlock: 'downloads-panel--bootable-usb-tab',
        tabBlockBtn: 'downloads-panel--bootable-usb-tab-btn',
        tabImage: 'downloads-panel--bootable-usb-tab-image',
        tabTitle: 'downloads-panel--bootable-usb-tab-title',
        createBtn: 'downloads-panel--bootable-usb-tab-create-btn'
      }
    }
  };

  /** Typed template tokens */
  public readonly tabContentToken: {
    builds: Build[];
    type: AgentType;
    beta: boolean;
  };
  public readonly newBuildSectionToken: {
    section: BuildDetails;
    isSandbox: boolean;
  };

  public allowSpecificBuild = true;
  public tabItems = [];
  public buildList = new BehaviorSubject<Build[]>(null);
  public backupBuilds$ = this.getBuildsByAgentType(AgentType.Backup);
  public rmmBuilds$ = this.getBuildsByAgentType(AgentType.RMM);
  public raBuilds$ = this.getBuildsByAgentType(AgentType.RA);
  public ndBuilds$ = this.getBuildsByAgentType(AgentType.NetworkDiscovery);
  public openedPanelIds = [];
  public settingsForm = new FormGroup({
    isSandboxEnabled: new FormControl(),
    isAutoUpdateEnabled: new FormControl(),
    isAutoGenerateEnabled: new FormControl()
  });
  public buildsInProgress: BuildType[] = [];
  public licensedBrandList: Brand[] = [];
  public currentUser: Administrator;
  public currentCopingStatus: CopyingStatus = null;
  public isSettingsButtonVisible = null;
  public newVersionsAlerts$ = this.buildsFacade.newVersionsAlerts$;
  public updateDisabledCountText = '';

  get isBuilding(): boolean {
    return !!this.buildsInProgress?.length;
  }

  get isSandboxEnabled(): boolean {
    return this.settingsForm.get('isSandboxEnabled').value;
  }

  @ViewChild('bootableUSBDescription') bootableUSBDescription: TemplateRef<any>;
  @ViewChild(TabsetDirective, { read: TabsetDirective, static: false }) tabset: TabsetDirective;
  @ViewChildren('copyTooltip') copyTooltips: QueryList<NgbTooltip>;

  constructor() {
    super();
    this.allowSpecificBuild = JSON.parse(localStorage.getItem(DPSpecificMessageKey)) !== false;
  }

  ngOnInit(): void {
    this.initPanelOpeningSubscription();
    this.initUserSubscription();
    this.initSettingsFormChangesSubscription();
    this.initBuildsSubscriptions();
  }

  panelOpened() {
    this.loadExcludeStatStream().subscribe({ next: (res) => this.updateExcludedText(res), error: noop });
  }

  public generateBuild(build: Build, isSandboxBuildRequests = false): void {
    this.buildService
      .requestBuild({
        buildsTypeList: [build.mbsBuildType],
        isBuildDestinationSandbox: isSandboxBuildRequests
      })
      .subscribe((res) => this.handleResponse(res));
  }

  public onRemoveBuildHandle(): void {
    this.buildsFacade.updateBuildList();
  }

  public getNewBuildSection(build: Build): BuildDetails {
    return this.isSandboxEnabled ? build?.sandbox : build?.public;
  }

  public onBuildPanelClick({ panelId, opening }: { panelId: string; opening: boolean }) {
    if (opening) {
      this.openedPanelIds = [panelId];
    }
  }

  public onOpenQuickRestoreButtonClick() {
    const settings: ModalSettings = {
      footer: {
        okButton: { show: false },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.close', { format: 'title' }) }
      },
      data: {
        title: this.i18nPipe.transform('shared:sidePanelDownloads.creteBootableUSBModalTitle')
      }
    };
    const modal = this.modalService.openRef(QuickRestoreSchemaModalComponent, settings);

    modal.componentInstance.downloadLink = this.buildList
      .getValue()
      .find((build) => build.mbsBuildType === BuildType.QuickRestore)?.public?.downloadLink;
    modal.componentInstance.createSchemaParams = {
      ownerId: this.currentUser?.ProviderInfo?.Id,
      CreateRecoveryDisk: 'True'
    };
    modal.componentInstance.bootableUSBDescription = this.bootableUSBDescription;
  }

  public onTabUpdate(event: { nextId: string }): void {
    this.buildsFacade.onTabVisit(event.nextId);
  }

  public specificDownloadClose(): void {
    this.allowSpecificBuild = false;

    localStorage.setItem(DPSpecificMessageKey, JSON.stringify(false));
  }

  public specificDownloadClick(event: Event, type: AgentType): void {
    event.preventDefault();

    const modal = this.modalService.openRef(AddComputerComponent, { size: MbsSize.lg });
    modal.componentInstance.mode = type;
    this.close.emit();
  }

  private getBuildsByAgentType(agentType: AgentType): Observable<Build[]> {
    return this.buildList.pipe(map((builds) => builds?.filter((build) => build.applicationId === agentType)));
  }

  private initUserSubscription(): void {
    this.auth.currentUser$.pipe(filter(Boolean), untilDestroyed(this)).subscribe((admin) => {
      this.currentUser = admin;
      this.modifyTabsItems();
    });
  }

  private initPanelOpeningSubscription() {
    this.genericPanel.panelOpened.pipe(untilDestroyed(this)).subscribe(() => {
      this.initBrands().subscribe();
    });
  }

  private initBrands(): Observable<Brand[]> {
    return this.brandsFacade.getActive$().pipe(
      untilDestroyed(this),
      filter((brandList) => !!brandList),
      tap((brandList) => {
        this.licensedBrandList = brandList;
      })
    );
  }

  private initSettingsFormChangesSubscription(): void {
    this.settingsForm.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((first, second) => isEqual(first, second)),
        filter(
          (settings) => !isNil(settings.isSandboxEnabled) && !isNil(settings.isAutoUpdateEnabled) && !isNil(settings.isAutoGenerateEnabled)
        ),
        mergeMap((settings) => {
          return this.buildService.updateDownloadSettings(settings);
        })
      )
      .subscribe(() => this.buildsFacade.updateBuildList());
  }

  private initBuildsSubscriptions() {
    this.buildsFacade.builds$.pipe(untilDestroyed(this)).subscribe((builds) => {
      this.buildList.next(builds);
    });

    this.buildsFacade.buildsInProgress$.pipe(untilDestroyed(this)).subscribe((buildsInProgress) => {
      this.buildsInProgress = buildsInProgress;
    });

    this.buildsFacade.buildsSettings$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((first, second) => isEqual(first, second))
      )
      .subscribe(({ isSettingsButtonVisible, isAutoUpdateEnabled, isSandBoxAvailable, isAutoGenerateEnabled }) => {
        this.isSettingsButtonVisible = isSettingsButtonVisible;

        this.settingsForm.patchValue(
          {
            isSandboxEnabled: isSandBoxAvailable,
            isAutoUpdateEnabled: isAutoUpdateEnabled,
            isAutoGenerateEnabled: isAutoGenerateEnabled
          },
          { emitEvent: false }
        );
      });
  }

  private handleResponse(res): void {
    this.toastService[res.success ? 'success' : 'error'](res.message);
  }

  private modifyTabsItems(): void {
    this.tabItems = getTabsConfig(this.i18nPipe, this.ability).reduce((acc, item) => {
      if (item.id === AgentType.RMM) {
        return this.ability.can('read', PermissionsEnum.Rmm) || this.auth.isInternalAdmin ? [...acc, item] : [...acc];
      }

      if (item.id === AgentType.Backup || item.id === 'bootableUSB') {
        return this.ability.can('read', PermissionsEnum.Backup) || this.auth.isInternalAdmin ? [...acc, item] : [...acc];
      }

      if (item.id === AgentType.RA) {
        return this.currentUser.IsProvider || this.ability.can('read', PermissionsEnum.ShowRemoteConnection) || this.auth.isInternalAdmin
          ? [...acc, item]
          : [...acc];
      }

      return [...acc, item];
    }, []);
  }

  public selectComputers(): void {
    from(
      this.modalService.openCustom(UtilitySelectionModalComponent, {
        data: {
          model: [],
          modalTitle: this.i18nPipe.transform('shared:sidePanelDownloads.options.computerModalTitle'),
          applyTo: UtilityModalSelectEntityType.computers,
          noDataText: this.i18nPipe.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectComputersModalNoComputersDesc'),
          saveButtonText: this.i18nPipe.transform('shared:buttons.save'),
          osType: null,
          agentType: null,
          multipleSelect: true,
          isSpecificCase: true,
          optionalOrderingParams: { orderBy: 'autoupdatedisabled:desc' },
          selectedValue: { name: 'autoUpdateDisabled', value: true }
        },
        size: MbsSize.lg,
        collapsing: true
      })
    )
      .pipe(
        switchMap((selected: { hid: string; autoupdatedisabled: boolean }[]) => {
          if (selected.length) {
            return this.computersFacade.setAutoUpdate({
              filter: null,
              autoupdatedisabled: false,
              autoupdaterequest: selected
            });
          }

          return of(null);
        }),
        switchMap(() => this.loadExcludeStatStream())
      )
      .subscribe({
        next: (res) => {
          this.updateExcludedText(res);
          this.toastService.success(this.i18nPipe.transform('shared:sidePanelDownloads.options.saved'));
        },
        error: () => noop
      });
  }

  updateExcludedText(res: ComputersStatisticModel) {
    if (res.versionStatus?.autoUpdateDisabled) {
      const computerText = this.i18nPipe.transform(`shared:common.computer${res.versionStatus.autoUpdateDisabled > 1 ? '_other' : '_one'}`);
      this.updateDisabledCountText = `${res.versionStatus.autoUpdateDisabled} ${computerText}`;
    } else {
      this.updateDisabledCountText = this.i18nPipe.transform('shared:rmm.groupActions.stepApplyTo.selectComputers');
    }
  }

  loadExcludeStatStream() {
    return this.computersStatisticService.getComputersStatistic({});
  }
}
